import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../components/Button";
import DataTable from "../../../components/DataTable";
import DataTableServerSideRendering from "../../../components/DataTable/DataTableServerSideRendering";
import EmptyState from "../../../components/EmptyState/EmptyState";
import { Input } from "../../../components/Inputs";
import Loading from "../../../components/Loading/Loading";
import Pill from "../../../components/Pill";
import {
  convertAmountToFrontend,
  convertNumberToFrontend,
} from "../../../helpers/convert";
import {
  getAllContractsRequested,
  setAllContractSelected,
  setContractFilter,
} from "../../../redux/actions/contracts";
import {
  getSystemParticipantMatchesRequested,
  resetInitialState,
} from "../../../redux/actions/offers";

export default function MarketResults({ t }) {
  const dispatch = useDispatch();
  const organization = useSelector(
    (state) => state.authorization.user.organization
  );
  const {
    all: contracts,
    total: contractsTotal,
    loading: contractsLoading,
    allSelected: selectedContract,
    filter,
  } = useSelector((state) => state.contracts);

  const marketMatchesForMarketResult = useSelector(
    (state) => state.offers.marketMatchesForMarketResult
  );

  const marketMatchesLoading = useSelector((state) => state.offers.loading);

  const { systemParticipantMatchesForMarketResult } = useSelector(
    (state) => state.offers
  );
  const {
    systemParticipantMatches,
    totalPageCount: systemParticipantMatchesTotalPageCount,
  } = systemParticipantMatchesForMarketResult;

  const systemParticipantMatchesLoading = useSelector(
    (state) => state.offers.systemParticipantMatchesLoading
  );

  const [activePage, setActivePage] = useState(0);
  const [activePage2, setActivePage2] = useState(0);
  const [sortedColumn, setSortedColumn] = useState("productionPeriod");
  const [sortDirection, setSortDirection] = useState("desc");
  const [sortedColumn2, setSortedColumn2] = useState("productionPeriod");
  const [sortDirection2, setSortDirection2] = useState("desc");
  const [selectedContact, setSelectedContact] = useState({});

  const contractsColumns = [
    {
      name: t("pages.default.marketResults.table.contractName"),
      selector: "contractName",
      sortable: true,
    },
    {
      name: t("pages.default.marketResults.table.bestBuyAmount"),
      selector: "bestBuyAmount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.bestBuyAmount);
      },
    },
    {
      name: t("pages.default.marketResults.table.bestSpread"),
      selector: "bestSpread",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({ num: row.bestSpread, digit: 2 });
      },
    },
    {
      name: t("pages.default.marketResults.table.bestSellAmount"),
      selector: "bestSellAmount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.bestSellAmount);
      },
    },
    {
      name: t("pages.default.marketResults.table.maxMatchPrice"),
      selector: "maxMatchPrice",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({ num: row.maxMatchPrice, digit: 2 });
      },
    },
    {
      name: t("pages.default.marketResults.table.minMatchPrice"),
      selector: "minMatchPrice",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({ num: row.minMatchPrice, digit: 2 });
      },
    },
    {
      name: t("pages.default.marketResults.table.lastMatchAmount"),
      selector: "lastMatchAmount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.lastMatchAmount);
      },
    },
    {
      name: t("pages.default.marketResults.table.lastMatchPrice"),
      selector: "lastMatchPrice",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({ num: row.lastMatchPrice, digit: 2 });
      },
    },
    {
      name: t("pages.default.marketResults.table.tradingVolume"),
      selector: "tradingVolume",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.tradingVolume);
      },
    },
    {
      name: t("pages.default.marketResults.table.weightedAverage"),
      selector: "weightedAverage",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({ num: row.weightedAverage, digit: 2 });
      },
    },
    {
      name: t("pages.default.marketResults.table.status"),
      selector: "status",
      sortable: true,
      cell: (row) => {
        return row.status === "ACTIVE" ? (
          <Pill
            type="success"
            label={t("pages.default.offerOperations.active")}
            emphasis
          />
        ) : (
          <Pill
            type="important"
            label={t("pages.default.offerOperations.passive")}
            emphasis
          />
        );
      },
    },
  ];

  const marketMatchesColumns = [
    {
      name: t("pages.default.marketResults.table.contract"),
      selector: "contractName",
      sortable: true,
    },
    {
      name: t("pages.default.marketResults.matchDate"),
      selector: "matchDate",
      sortable: true,
      cell: (row) => {
        return moment(row.matchDate).format("DD.MM.YYYY HH:mm:ss");
      },
    },
    {
      name: t("pages.default.marketResults.price"),
      selector: "matchPrice",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({ num: row.matchPrice, digit: 2 });
      },
    },
    {
      name: t("pages.default.marketResults.quantity"),
      selector: "matchAmount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.matchAmount);
      },
    },
  ];

  const systemParticipantMatchesColumns = [
    {
      name: t("pages.default.marketResults.table.contract"),
      selector: "contractName",
      sortable: true,
    },
    {
      name: t("pages.default.marketResults.matchDate"),
      selector: "matchDate",
      sortable: true,
      cell: (row) => {
        return moment(row.matchDate).format("DD.MM.YYYY HH:mm:ss");
      },
    },
    {
      name: t("pages.default.marketResults.price"),
      selector: "matchPrice",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({ num: row.matchPrice, digit: 2 });
      },
    },
    {
      name: t("pages.default.marketResults.quantity"),
      selector: "matchQuantity",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.matchQuantity);
      },
    },
    {
      name: t("pages.default.marketResults.table.orderType"),
      selector: "orderType",
      sortable: true,
      cell: (row) => {
        return row.orderType === "BUY"
          ? t("ui.default.offerTab.buy")
          : t("ui.default.offerTab.sell");
      },
    },
  ];

  // initial load of contracts
  useEffect(() => {
    dispatch(resetInitialState());
    requestContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterChange = (e) => {
    // deep copy
    let filterCopy = JSON.parse(JSON.stringify(filter));
    filterCopy = {
      ...filterCopy,
      [e.target.name]: e.target.value,
    };
    dispatch(setContractFilter(filterCopy));
  };

  const requestContracts = () => {
    dispatch(getAllContractsRequested());
  };

  const contractSelection = ({
    row,
    activePage = 0,
    selector2,
    direction2,
  }) => {
    if (Boolean(row)) {
      dispatch(
        setAllContractSelected({
          contract: row ?? selectedContact,
          isMarketResult: true,
        })
      );
      dispatch(
        getSystemParticipantMatchesRequested({
          page: activePage,
          contractId: row?.id,
          organizationId: organization.organizationId,
          isMarketResult: true,
        })
      );
      setActivePage2(activePage);

      setSelectedContact(row);
    } else {
      dispatch(
        getSystemParticipantMatchesRequested({
          page: activePage,
          contractId: selectedContact?.id,
          organizationId: organization.organizationId,
          isMarketResult: true,
        })
      );
    }
  };
  const contractRowSelected = (row) => {
    if (selectedContract) return row.id === selectedContract.id;
    else return false;
  };

  const handleClick = (activePage, selector, direction) => {
    setActivePage(activePage);
    dispatch(
      getAllContractsRequested({
        page: activePage,
        selector: selector,
        direction: direction,
      })
    );
  };
  return (
    <>
      <h5 className="km__u--text-has-emphasis">
        {t("pages.default.marketResults.title")}
      </h5>
      <div className="km__p--card">
        <div className="km__p--market-results-contracts-header">
          <p className="km__u--text_title">
            {t("pages.default.marketResults.contracts")}
          </p>
          <div className="km__p--market-results-contracts-header-filter">
            <Input
              type="month"
              placeholder={t("pages.default.marketResults.sessionPeriod")}
              name="period"
              value={filter.period}
              onChange={filterChange}
            />
            <Button label={t("ui.default.filter")} onClick={requestContracts} />
          </div>
        </div>
        {/* Kontratlar */}
        {!contractsLoading && contracts.length > 0 && (
          <DataTableServerSideRendering
            columns={contractsColumns}
            data={contracts}
            rowKey="id"
            selectableRow
            rowSelection={(row) => contractSelection({ row })}
            isRowSelected={(row) => contractRowSelected({ row })}
            sortedColumn={sortedColumn}
            setSortedColumn={setSortedColumn}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            pagination
            activePage={activePage}
            totalPageCount={contractsTotal?.totalPageCount}
            handleFetchData={handleClick}
          />
        )}
        {!contractsLoading && contracts.length === 0 && (
          <EmptyState
            icon="file-text"
            text={t("pages.default.marketResults.contractNotFound")}
          />
        )}
        {contractsLoading && (
          <Loading text={t("pages.default.marketResults.contractLoading")} />
        )}
      </div>

      <div className="km__p--market-results-row">
        {/* Piyasa Eşleşmeleri */}
        {selectedContract ? (
          <div className="km__p--market-results-matches w-50">
            <div className="km__p--market-results-contracts-header">
              <p className="km__u--text_title">
                {t("pages.default.marketResults.marketMatches")} -{" "}
                {selectedContract.contractName}
              </p>
            </div>
            {!marketMatchesLoading &&
              marketMatchesForMarketResult &&
              marketMatchesForMarketResult?.length > 0 && (
                <DataTable
                  rowKey="id"
                  pagination
                  paginationPerPage={10}
                  columns={marketMatchesColumns}
                  data={marketMatchesForMarketResult}
                />
              )}
            {!marketMatchesLoading &&
              marketMatchesForMarketResult &&
              marketMatchesForMarketResult.length === 0 && (
                <EmptyState
                  icon="file-text"
                  text={t("pages.default.marketResults.notFound")}
                />
              )}
            {marketMatchesLoading && (
              <Loading text={t("pages.default.marketResults.loading")} />
            )}
          </div>
        ) : null}
        {/* Eşleşmelerim */}
        {selectedContract ? (
          <div className="km__p--market-results-matches w-50">
            <div className="km__p--market-results-contracts-header">
              <p className="km__u--text_title">
                {t("pages.default.marketResults.myMatches")} -{" "}
                {selectedContract.contractName}
              </p>
            </div>
            {!systemParticipantMatchesLoading &&
              systemParticipantMatches &&
              systemParticipantMatches.length > 0 && (
                <DataTableServerSideRendering
                  columns={systemParticipantMatchesColumns}
                  data={systemParticipantMatches}
                  rowKey="id"
                  sortedColumn={sortedColumn2}
                  setSortedColumn={setSortedColumn2}
                  sortDirection={sortDirection2}
                  setSortDirection={setSortDirection2}
                  pagination
                  activePage={activePage2}
                  totalPageCount={systemParticipantMatchesTotalPageCount}
                  handleFetchData={(activePage) =>
                    contractSelection({
                      activePage,
                      selector2: sortedColumn,
                      direction2: sortDirection,
                    })
                  }
                />
              )}
            {!systemParticipantMatchesLoading &&
              systemParticipantMatches &&
              systemParticipantMatches.length === 0 && (
                <EmptyState
                  icon="file-text"
                  text={t("pages.default.marketResults.matchesNotFound")}
                />
              )}
            {systemParticipantMatchesLoading && (
              <Loading text={t("pages.default.marketResults.matchesLoading")} />
            )}
          </div>
        ) : null}
      </div>
    </>
  );
}
